import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { 
  BookOpen, 
  Play,
  Trophy,
  Target,
  Star,
  Flame,
  CheckCircle,
  ArrowRight,
  BarChart3,
  Users
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { calculateLearningStreak, getLastCompletionDate } from "@/utils/streakUtils";
import CompetitiveStats from "@/components/dashboard/CompetitiveStats";
import CentseScoreCard from "@/components/dashboard/CentseScoreCard";

/**
 * Learn Dashboard - Focused on educational progress and achievements
 * 
 * Features:
 * - Learning progress and streak tracking
 * - Educational achievements and milestones
 * - Competitive stats with friends
 * - Quick access to learning activities
 * - Progress visualization
 */
const LearnDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [username, setUsername] = useState<string>("");

  // Fetch user profile to display personalized greeting
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', user.id)
            .single();
          
          if (profile?.username) {
            setUsername(profile.username);
          }
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
  }, []);

  // Fetch comprehensive learning progress with completion tracking
  const { data: progressData } = useQuery({
    queryKey: ['comprehensiveLearningProgress'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      console.log("📊 Fetching comprehensive learning progress for user:", user.id);

      // Get all user progress records
      const { data: userProgress, error: progressError } = await supabase
        .from('user_progress')
        .select('*')
        .eq('user_id', user.id);

      if (progressError) {
        console.error("Error fetching user progress:", progressError);
        throw progressError;
      }

      // Get all lessons to calculate total counts
      const { data: allLessons, error: lessonsError } = await supabase
        .from('lessons')
        .select(`
          id,
          title,
          module_id,
          modules!inner(
            id,
            title,
            topic_id,
            topics!inner(
              id,
              title
            )
          )
        `)
        .eq('status', 'active');

      if (lessonsError) {
        console.error("Error fetching lessons:", lessonsError);
        throw lessonsError;
      }

      console.log("📋 Found lessons:", allLessons?.length || 0);
      console.log("✅ User progress records:", userProgress?.length || 0);

      const completedLessons = userProgress?.filter(p => p.status === 'completed') || [];
      const inProgressLessons = userProgress?.filter(p => p.status === 'in_progress') || [];

      // Calculate real learning streak based on actual completion dates
      const learningStreak = calculateLearningStreak(userProgress || []);
      const lastCompletionDate = getLastCompletionDate(userProgress || []);

      console.log("🔥 Calculated learning streak:", learningStreak);
      console.log("📅 Last completion date:", lastCompletionDate);

      // Calculate module completion statistics
      const moduleStats = {};
      const topicStats = {};

      allLessons?.forEach(lesson => {
        const moduleId = lesson.module_id;
        const topicId = lesson.modules?.topic_id;
        const moduleName = lesson.modules?.title;
        const topicName = lesson.modules?.topics?.title;

        // Initialize module stats if not exists
        if (!moduleStats[moduleId]) {
          moduleStats[moduleId] = {
            name: moduleName,
            total: 0,
            completed: 0,
            topicId: topicId,
            topicName: topicName
          };
        }
        moduleStats[moduleId].total += 1;

        // Initialize topic stats if not exists
        if (!topicStats[topicId]) {
          topicStats[topicId] = {
            name: topicName,
            total: 0,
            completed: 0
          };
        }
        topicStats[topicId].total += 1;

        // Check if lesson is completed
        const isCompleted = completedLessons.some(p => p.lesson_id === lesson.id);
        if (isCompleted) {
          moduleStats[moduleId].completed += 1;
          topicStats[topicId].completed += 1;
        }
      });

      console.log("📈 Module completion stats:", moduleStats);
      console.log("📈 Topic completion stats:", topicStats);

      return {
        totalLessons: allLessons?.length || 0,
        completedLessons: completedLessons.length,
        inProgressLessons: inProgressLessons.length,
        learningStreak,
        lastCompletionDate,
        moduleStats,
        topicStats,
        userProgress: userProgress || []
      };
    }
  });

  // Fetch user points for level calculation
  const { data: userPoints } = useQuery({
    queryKey: ['userPoints'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_points')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    }
  });

  // Calculate statistics using real data from database
  const totalLessons = progressData?.totalLessons || 0;
  const completedLessons = progressData?.completedLessons || 0;
  const inProgressLessons = progressData?.inProgressLessons || 0;
  const learningStreak = progressData?.learningStreak || 0;
  const totalPoints = userPoints?.total_points || 0;
  const overallProgress = totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;
  const progressToNext = ((totalPoints % 100) / 100) * 100;

  // Get top performing modules/topics for display
  const topModules = Object.values(progressData?.moduleStats || {})
    .sort((a: any, b: any) => (b.completed / b.total) - (a.completed / a.total))
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8 bg-background">
        
        {/* Hero Section with Learning Focus */}
        <div className="space-y-6">
          {/* Welcome Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-primary/10 rounded-lg">
                <BookOpen className="h-6 w-6 text-primary" />
              </div>
              <Badge variant="secondary" className="bg-primary/10 text-primary">
                Learning Hub
              </Badge>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900">
              Ready to learn{username ? `, ${username}` : ''}? 📚
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl">
              You've completed {completedLessons} out of {totalLessons} lessons ({overallProgress.toFixed(0)}% complete)
            </p>
            
            {/* Overall Progress Bar */}
            <div className="max-w-md">
              <Progress value={overallProgress} className="h-3" />
              <p className="text-sm text-gray-500 mt-2">
                Overall Learning Progress
              </p>
            </div>

            {/* Primary Learning Action */}
            <div className="pt-4">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                onClick={() => navigate("/learn")}
              >
                <Play className="mr-2 h-5 w-5" />
                Continue Learning
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Centse Score Profile Card - Now directly under hero text */}
          <div className="max-w-2xl">
            <CentseScoreCard />
          </div>
        </div>

        {/* Learning Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="bg-background border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Lessons Completed</p>
                  <p className="text-3xl font-bold text-gray-900">{completedLessons}</p>
                  <p className="text-xs text-gray-500">out of {totalLessons}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-background border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Learning Streak</p>
                  <p className="text-3xl font-bold text-gray-900">{learningStreak}</p>
                  <p className="text-xs text-gray-500">days in a row</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <Flame className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-background border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">In Progress</p>
                  <p className="text-3xl font-bold text-gray-900">{inProgressLessons}</p>
                  <p className="text-xs text-gray-500">lessons started</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <BookOpen className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-background border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Learning Points</p>
                  <p className="text-3xl font-bold text-gray-900">{totalPoints}</p>
                  <p className="text-xs text-gray-500">earned</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Star className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Competitive Stats with Friends */}
        <CompetitiveStats />

        {/* Module Progress Section */}
        {topModules.length > 0 && (
          <Card className="bg-background border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Module Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topModules.map((module: any, index) => {
                  const completion = (module.completed / module.total) * 100;
                  return (
                    <div key={index} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium text-gray-900">{module.name}</p>
                          <p className="text-sm text-gray-500">{module.topicName}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{module.completed}/{module.total}</p>
                          <p className="text-xs text-gray-500">{completion.toFixed(0)}% complete</p>
                        </div>
                      </div>
                      <Progress value={completion} className="h-2" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Learning Progress Level */}
        <Card className="bg-background border-gray-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-900">Learning Level</CardTitle>
              <Badge variant="secondary" className="bg-primary/10 text-primary">
                Level {Math.floor(totalPoints / 100) + 1}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between text-sm text-gray-600">
                <span>{totalPoints} points</span>
                <span>{Math.ceil((totalPoints + 1) / 100) * 100} points to next level</span>
              </div>
              <Progress value={progressToNext} className="h-2" />
              <p className="text-xs text-gray-500 text-center">
                Complete lessons and knowledge checks to earn more points!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Learning Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card 
            className="group cursor-pointer bg-background border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            onClick={() => navigate("/learn")}
          >
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="p-3 bg-primary/10 rounded-lg w-fit group-hover:scale-110 transition-transform">
                  <BookOpen className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Interactive Lessons</h3>
                  <p className="text-sm text-gray-600">Learn financial concepts through engaging content</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="group cursor-pointer bg-background border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            onClick={() => navigate("/play")}
          >
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="p-3 bg-green-100 rounded-lg w-fit group-hover:scale-110 transition-transform">
                  <Target className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Learning Games</h3>
                  <p className="text-sm text-gray-600">Practice skills through fun financial games</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="group cursor-pointer bg-background border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
            onClick={() => navigate("/leaderboards")}
          >
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="p-3 bg-yellow-100 rounded-lg w-fit group-hover:scale-110 transition-transform">
                  <Trophy className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Compete & Learn</h3>
                  <p className="text-sm text-gray-600">See how you rank against friends and peers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LearnDashboard;